import { createContext, useContext, useMemo } from "react";
import { useWindowDimensions } from "react-native";

// Define interface for editor context
export interface EditorContextType {
  ref?: any;
  insertContent: (content: string) => void;
  setContent: (content: string) => void;
  getContent: () => string;
  appendContent?: (content: string) => void;
  prependContent?: (content: string) => void;
  clearContent?: () => void;
  isConnected?: boolean;
  // Add screen dimensions for responsive content
  contentWidth?: number;
  contentHeight?: number;
}

// Define the complete toolbar context interface
export interface ToolbarContextType {
  calendarContext: any;
  calendarHeader: any;
  setCalendarHeader: (header: any) => void;
  setCalendarContext: (month: Date) => void;
  setHierarchySocket: (socket: any) => void;
  hierarchySocket: any;
  ref: any;
  draggedItem: any;
  setDraggedItem: (item: any) => void;
  setRef: (newRef: any) => void; // Updated to accept a ref parameter
  optionsRef: any;
  setOptionsRef: (ref: unknown) => void;
  reportRef: any;
  setReportRef: () => void;
  projectContext: any;
  setProjectContext: (project: any) => void;
  // Enhanced editor context for knowledge editor integration
  editorContext: EditorContextType | null;
  setEditorContext: (editor: EditorContextType | null) => void;
  // Mobile messages animation control
  mobileMessagesAnimation?: any;
  mobileMessagesTranslateX?: any;
}

export const ToolbarContext = createContext<ToolbarContextType>({
  calendarContext: null,
  calendarHeader: null,
  setCalendarHeader: (header: any) => { },
  setCalendarContext: (month: Date) => { },
  setHierarchySocket: (socket) => { },
  hierarchySocket: null,
  ref: null,
  draggedItem: null,
  setDraggedItem: (item: any) => { },
  setRef: (newRef: any) => { }, // Updated to accept a ref parameter
  optionsRef: null,
  setOptionsRef: (ref: unknown) => { },
  reportRef: null,
  setReportRef: () => { },
  projectContext: null,
  setProjectContext: (project) => { },
  // Enhanced editor context for knowledge editor integration
  editorContext: null,
  setEditorContext: (editor: EditorContextType | null) => { },
  // Mobile messages animation control
  mobileMessagesAnimation: undefined,
  mobileMessagesTranslateX: undefined,
});

// Custom hook for easier editor context usage - performance optimized
export const useEditorContext = () => {
  const { editorContext, setEditorContext } = useContext(ToolbarContext);
  const dimensions = useWindowDimensions();
  
  // Memoize screen dimensions to prevent unnecessary re-renders
  const memoizedDimensions = useMemo(() => ({
    width: dimensions.width,
    height: dimensions.height
  }), [dimensions.width, dimensions.height]);
  
  // Memoize the return object to prevent recreation on every render
  return useMemo(() => ({
    editorContext,
    setEditorContext,
    isEditorConnected: !!editorContext,
    // Screen dimensions for responsive content
    contentWidth: memoizedDimensions.width,
    contentHeight: memoizedDimensions.height,
    screenDimensions: memoizedDimensions,
    
    // Helper methods for content manipulation - memoized for performance
    insertContent: (content: string) => {
      if (editorContext?.insertContent) {
        editorContext.insertContent(content);
        return true;
      }
      return false;
    },
    
    setContent: (content: string) => {
      if (editorContext?.setContent) {
        editorContext.setContent(content);
        return true;
      }
      return false;
    },
    
    getContent: () => {
      return editorContext?.getContent() || '';
    },
    
    appendContent: (content: string) => {
      if (editorContext?.appendContent) {
        editorContext.appendContent(content);
        return true;
      } else if (editorContext?.insertContent) {
        // Fallback to insertContent if appendContent not available
        editorContext.insertContent(content);
        return true;
      }
      return false;
    },
    
    clearContent: () => {
      if (editorContext?.clearContent) {
        editorContext.clearContent();
        return true;
      } else if (editorContext?.setContent) {
        // Fallback to setContent with empty string
        editorContext.setContent('');
        return true;
      }
      return false;
    },
    
    // Helper method to insert responsive HTML content
    insertResponsiveHTML: (htmlContent: string) => {
      const responsiveHTML = `
        <div style="
          max-width: ${memoizedDimensions.width}px;
          width: 100%;
          box-sizing: border-box;
          font-size: 16px;
          line-height: 1.6;
        ">
          ${htmlContent}
        </div>
      `;
      
      if (editorContext?.insertContent) {
        editorContext.insertContent(responsiveHTML);
        return true;
      }
      return false;
    },
    
    // Helper method to insert responsive image
    insertResponsiveImage: (imageUrl: string, alt: string = '') => {
      const responsiveImg = `
        <img src="${imageUrl}" 
             alt="${alt}"
             style="
               max-width: ${memoizedDimensions.width - 40}px;
               width: 100%;
               height: auto;
               display: block;
               margin: 10px 0;
             " />
      `;
      
      if (editorContext?.insertContent) {
        editorContext.insertContent(responsiveImg);
        return true;
      }
      return false;
    }
  }), [editorContext, memoizedDimensions]);
};
